import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { Download, Chrome, CheckCircle2, Smartphone, ArrowRight, ChevronDown } from "lucide-react";
import { usePageMeta } from "@/lib/seo";

const CHROME_STORE_URL = "https://chromewebstore.google.com/detail/dodbbengpljjkpfapnklccapeobanpca";
const DOWNLOAD_URL = `${process.env.REACT_APP_BACKEND_URL}/api/download/extension?direct=1`;

// Detect mobile — Chrome extensions don't work on mobile Chrome, so we short-circuit
// the install flow and just push users to the URL scanner instead.
function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(navigator.userAgent);
}

export default function Install() {
  usePageMeta({
    title: "Install the Chrome Extension — MonetizeGPS",
    description: "Install the MonetizeGPS Chrome extension: one click analyzes the site you're browsing. 30-second setup, works on any public site.",
    path: "/install",
  });
  const [isMobile, setIsMobile] = useState(false);
  const [showManual, setShowManual] = useState(false);

  useEffect(() => { setIsMobile(isMobileDevice()); }, []);

  return (
    <div className="min-h-screen grain" data-testid="install-page">
      <Header />
      {isMobile && (
        <div
          className="mx-auto max-w-4xl px-6 md:px-10 pt-8"
          data-testid="mobile-install-notice"
        >
          <div className="border-2 border-[#111] bg-[#FF331F] text-white p-5 md:p-6 shadow-brutal-lg">
            <div className="flex items-start gap-4">
              <Smartphone size={28} className="shrink-0 mt-1" />
              <div className="flex-1">
                <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] opacity-80">
                  ▸ You&apos;re on mobile
                </div>
                <div className="mt-1 font-serif-display text-2xl md:text-3xl font-bold leading-tight">
                  Chrome extensions don&apos;t run on phones.
                </div>
                <p className="mt-2 text-sm opacity-90">
                  Use the URL scanner on this device — same intelligence, no install. Or open this page on your desktop / laptop to install the extension.
                </p>
                <Link
                  to="/"
                  className="mt-4 inline-flex items-center gap-2 bg-white text-[#111] px-5 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#111] hover:text-white transition-colors"
                  data-testid="mobile-scan-cta"
                >
                  Analyze a URL instead <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
      <main className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555] mb-3">
          ▸ Install · 30 seconds
        </div>
        <h1 className="font-serif-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.85]">
          Add MonetizeGPS<br />to Chrome.
        </h1>

        {/* STEP 1 — CHROME WEB STORE (PRIMARY) */}
        <div className="mt-10 border-2 border-[#111] bg-white shadow-brutal-lg p-6 md:p-8" data-testid="store-install-card">
          <div className="flex items-start gap-4">
            <Chrome size={36} className="text-[#111] shrink-0 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
                Step 01 / 02
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-none mt-1">
                One click. That&apos;s it.
              </h2>
              <p className="mt-3 text-[#333]">
                MonetizeGPS is officially on the <strong>Chrome Web Store</strong>. Click the button, then hit <strong>&quot;Add to Chrome&quot;</strong> on the store page. No zip files, no developer mode.
              </p>
              <a
                href={CHROME_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="mt-5 inline-flex items-center gap-3 bg-[#FF331F] text-white px-6 py-4 font-mono-plex text-sm uppercase tracking-[0.25em] hover:bg-[#111] transition-colors shadow-brutal"
                data-testid="chrome-store-button"
              >
                <Chrome size={18} /> Get it on the Chrome Web Store <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>

        {/* STEP 2 — PIN & ANALYZE */}
        <div className="mt-6 border-2 border-[#111] bg-white p-6 md:p-8" data-testid="step-pin">
          <div className="flex items-start gap-4">
            <CheckCircle2 size={36} className="text-[#0A8754] shrink-0 mt-1" />
            <div className="flex-1">
              <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
                Step 02 / 02
              </div>
              <h2 className="font-serif-display text-3xl md:text-4xl font-bold leading-none mt-1">
                Pin & analyze
              </h2>
              <p className="mt-3 text-[#222]">
                Click the puzzle-piece icon in your Chrome toolbar, then click the <strong>pin</strong> next to MonetizeGPS. Now click the MonetizeGPS icon on any website to analyze it.
              </p>
              <div className="mt-4 font-mono-plex text-xs text-[#555]">
                Keyboard shortcut: <span className="border border-[#111] px-2 py-0.5 ml-1">Ctrl + Shift + Y</span> (Windows) or <span className="border border-[#111] px-2 py-0.5 ml-1">⌘ + Shift + Y</span> (Mac)
              </div>
            </div>
          </div>
        </div>

        {/* MANUAL INSTALL FALLBACK (collapsed) */}
        <div className="mt-6 border-2 border-dashed border-[#888] bg-transparent" data-testid="manual-install-section">
          <button
            onClick={() => setShowManual(!showManual)}
            className="w-full flex items-center justify-between gap-3 px-6 py-4 font-mono-plex text-xs uppercase tracking-[0.25em] text-[#555] hover:text-[#111] transition-colors"
            data-testid="manual-install-toggle"
          >
            <span className="flex items-center gap-2"><Download size={14} /> Prefer manual install? (advanced — developer mode)</span>
            <ChevronDown size={16} className={`transition-transform ${showManual ? "rotate-180" : ""}`} />
          </button>
          {showManual && (
            <div className="px-6 pb-6" data-testid="manual-install-steps">
              <ol className="space-y-3 text-sm text-[#222]">
                <li className="flex gap-3">
                  <span className="font-mono-plex text-xs text-[#FF331F] mt-1">01</span>
                  <span>
                    <a
                      href={DOWNLOAD_URL}
                      download="monetizegps-extension.zip"
                      className="underline text-[#FF331F] font-semibold"
                      data-testid="download-button"
                    >
                      Download monetizegps-extension.zip
                    </a>{" "}
                    and <strong>unzip</strong> it. You&apos;ll get a folder with manifest.json, popup.html, icons, etc.
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono-plex text-xs text-[#FF331F] mt-1">02</span>
                  <span>Open <code className="font-mono-plex text-[12px] bg-[#F4F4F0] border border-[#ddd] px-1.5 py-0.5">chrome://extensions</code> in a new tab (paste it into the address bar).</span>
                </li>
                <li className="flex gap-3">
                  <span className="font-mono-plex text-xs text-[#FF331F] mt-1">03</span>
                  <span>Toggle <strong>Developer mode</strong> ON (top-right corner), click <strong>Load unpacked</strong>, and select the unzipped folder.</span>
                </li>
              </ol>
            </div>
          )}
        </div>

        <div className="mt-10 flex flex-col md:flex-row gap-4 items-start">
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest text-[#555] hover:text-[#FF331F]"
            data-testid="back-home"
          >
            ← Back to home
          </Link>
          <span className="hidden md:inline text-[#555]">·</span>
          <Link
            to="/terms"
            className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-widest text-[#555] hover:text-[#FF331F]"
            data-testid="terms-link"
          >
            Terms & privacy
          </Link>
        </div>
      </main>
    </div>
  );
}
